import { isBackendPath, toApiUrl } from '../config/api';

const SPECIAL_SCHEMES_RE = /^(data|blob|mailto|tel):/i;
const ABSOLUTE_URL_RE = /^[a-z][a-z\d+\-.]*:/i;
const LOCAL_DEV_HOST_RE = /^(localhost|127(?:\.\d{1,3}){3}|0\.0\.0\.0)$/i;
const FORCE_API_UPLOAD_ORIGIN =
  String(import.meta.env['VITE_USE_API_ORIGIN_FOR_UPLOADS'] || 'true')
    .trim()
    .toLowerCase() !== 'false';

function isUploadsPath(pathname: string): boolean {
  return pathname === '/uploads' || pathname.startsWith('/uploads/');
}

function shouldPreferSameOriginUploads(): boolean {
  if (FORCE_API_UPLOAD_ORIGIN || typeof window === 'undefined') return false;
  const host = (window.location?.hostname || '').trim();
  if (!host) return false;
  return !LOCAL_DEV_HOST_RE.test(host);
}

function resolveUploadsUrl(pathname: string, search: string = '', hash: string = ''): string {
  const pathWithQuery = `${pathname}${search}${hash}`;
  if (shouldPreferSameOriginUploads()) {
    return pathWithQuery;
  }
  return toApiUrl(pathWithQuery);
}

/**
 * Resolves a media/file URL for browser-safe asset loading.
 * Supports absolute URLs, backend-relative paths, and plain stored paths.
 * Upload assets resolve to backend API origin by default.
 */
export function resolveBackendAssetUrl(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  if (SPECIAL_SCHEMES_RE.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return trimmed;

  if (ABSOLUTE_URL_RE.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      if (isBackendPath(parsed.pathname)) {
        if (isUploadsPath(parsed.pathname)) {
          return resolveUploadsUrl(parsed.pathname, parsed.search, parsed.hash);
        }
        return toApiUrl(`${parsed.pathname}${parsed.search}${parsed.hash}`);
      }
    } catch {
      // Ignore and keep the original fallback behavior below.
    }
    return toApiUrl(trimmed);
  }

  if (trimmed.startsWith('#')) return trimmed;

  const normalized = `/${trimmed.replace(/^\/+/, '')}`;
  if (normalized === '/uploads' || normalized.startsWith('/uploads/')) {
    try {
      const parsed = new URL(normalized, 'http://localhost');
      return resolveUploadsUrl(parsed.pathname, parsed.search, parsed.hash);
    } catch {
      return resolveUploadsUrl(normalized);
    }
  }
  return toApiUrl(normalized);
}
